/* eslint-disable max-lines */
import * as React from 'react';
import AppTheme from '../../../theme/AppTheme';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import { brand } from '../../../theme/themePrimitives';
import Button from '@mui/material/Button';
import { CircularProgress } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import CustomInput from '../../../components/reusable/input';
import ErrorModal from '../../../components/modals/error_modal';
import ImageClass from '../../../assets';
import LoaderPage from '../../../components/loader';
import { LoginParams } from '../../../params/auth';
import { loginSchema } from '../../../schema/auth_schema';
import { loginThunk } from '../../../redux/thunks';
import { RouteChangeToastCleaner } from '../../../route_toast_cleaner';
import { toast } from 'react-toastify';
import Typography from '@mui/material/Typography';
import { AppDispatch, RootState } from '../../../redux/store';
import { Form, Formik } from 'formik';
import { styled, useTheme } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';

import 'react-toastify/dist/ReactToastify.css';

/**
 * @heading1: Login Page
 * @block_quote: This is the login page component.
 * @heading3: Styled Components.
 * @ol:
 * Styled page
 * Page Container
 * Image Container
 * Form Container
 * Styled Form
 * @separator:
 */
const StyledPage = styled(Box)(({ theme }) => ({
  padding: '10rem 5rem',
  display: 'flex',
  height: '100vh',
  [theme.breakpoints.down('sm')]: {
    padding: '5rem 0'
  }
}));

const StyledLink = styled('span')(() => ({
  'cursor': 'pointer',
  'color': brand[300],
  'whiteSpace': 'nowrap',
  '&:hover': {
    textDecoration: 'underline'
  }
}));

const PageContainer = styled(Box)(({ theme }) => ({
  flex: '1',
  display: 'flex',
  // height: '100vh',
  borderRadius: '10px',
  // border: '1px solid #ededed',
  // boxShadow: '1px 1px 1px 1px #f4f4f4',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column'
  }
}));

const ImageContainer = styled(Box)(({ theme }) => ({
  flex: 6,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: brand[300],
  borderTopLeftRadius: '10px',
  borderBottomLeftRadius: '10px',
  [theme.breakpoints.down('sm')]: {
    height: '50%',
    display: 'none'
  }
}));

const FormContainer = styled(Box)(({ theme }) => ({
  flex: 4,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  borderTop: '1px solid #ededed',
  borderRight: '1px solid #ededed',
  borderBottom: '1px solid #ededed',
  borderTopRightRadius: '10px',
  borderBottomRightRadius: '10px',
  padding: theme.spacing(4),
  [theme.breakpoints.down('sm')]: {
    height: '50%'
  }
}));

const StyledForm = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '1200px',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2)
}));

/**
 * @heading2: Login Page Component
 * @heading3: Variables
 * @ol:
 * initialValues : {LoginParams} - login initial values which are email and password.
 * dispatch : {AppDispatch} - The Redux dispatch function for triggering state updates.
 * navigate - Navigate for routing purpose.
 * isLoading - getting isLoading from a redux state/app.
 * token - getting token from a redux state/app.
 * @separator:
 */
const LoginPage = (props: { disableCustomTheme?: boolean }) => {
  const initialValues: LoginParams = {
    email: '',
    password: ''
  };
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const theme = useTheme();
  const { isLoading, token, currentUser } = useSelector((state: RootState) => state.app);

  const [loading, setLoading] = React.useState<boolean>(true);
  const location = useLocation();
  // const hasShownRef = React.useRef(false);
  /**
   * @heading3: Functions
   * @heading4: useEffect
   * @paragraph: Redirects the user to the dashboard if a valid token exists.
   */
  React.useEffect(() => {
    if (token) {
      if (currentUser?.client_id !== 0 && currentUser?.role === 'client') {
        navigate(`/app/client/${currentUser.client_id}/dashboard`);
      }
      navigate('/app/dashboard');
    }
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  }, [token, navigate, currentUser?.client_id, currentUser?.role]);

  // React.useEffect(() => {
  //   if (!hasShownRef.current && !token) {
  //     hasShownRef.current = true;
  //     setTimeout(() => {
  //       toast.error(
  //         <Box px={2}>
  //           As part of our system update, your password needs to be updated.
  //           <br />
  //           <span
  //             style={{ textDecoration: 'underline', color: '#fff', cursor: 'pointer', fontWeight: 500 }}
  //             onClick={() => navigate('/forget-password')}
  //           >
  //             Click here to reset your password
  //           </span>
  //         </Box>,
  //         // '🔐 As part of our system update, your password needs to be updated. Please reset your password to access your account.',
  //         {
  //           position: 'top-center',
  //           autoClose: false,
  //           closeOnClick: true,
  //           theme: 'colored',
  //           className: 'full-width-toast'
  //         }
  //       );
  //     }, 1000);
  //   }
  // }, [token]);

  React.useEffect(() => {
    if (location.state?.toastMessage) {
      toast.success(location.state.toastMessage);
    }
  }, [location]);


  /**
   * @heading4: handleSubmit
   * @paragraph: Performs login fucntionality
   * @bold: Paramter
   * @ol:
   * values : LoginParams - values are email and password
   * @separator:
   */
  const handleSubmit = async (values: LoginParams) => {
    if (values.email.trim() && values.password.trim()) {
      try {
        const response = await dispatch(loginThunk({
          email: values.email,
          password: values.password,
          navigate
        })).unwrap();
        const roleExist = ['organizationOwner', 'organizationAdmin', 'salesMember', 'salesManager', 'companyManager'].includes(response?.role as string);
        if (roleExist || response?.role === 'user') {
          navigate('/app/dashboard');
        } else {
          navigate(`/app/client/${response?.client_id || 0}/dashboard`);
        }
      } catch (e) {
        // error handle
      }
    }
  };

  if (loading) {
    return <LoaderPage />;
  }

  return (
    <AppTheme {...props}>
      <CssBaseline />
      <StyledPage>
        <PageContainer>
          {/* Image Section */}
          <ImageContainer position="relative">
            <Typography color="white" variant="h3" position="absolute" top="5rem" left="5rem" whiteSpace="wrap" width="50%">Acquisition & Fulfillment With The Push Of a Button</Typography>
            <Avatar
              sx={{ width: 'auto', height: '50vh', position: 'absolute', bottom: '0', right: '0' }}
              src={ImageClass.auth}
              variant="square"
            />
          </ImageContainer>

          {/* Login Form Section */}
          <FormContainer>
            <img src={theme.palette.mode === 'dark' ? ImageClass.logoDark : ImageClass.logoLight} alt="Company logo" height={80} />
            <Typography variant="h1" color={brand[300]} fontSize="32px" fontWeight="700" margin="20px 0" gutterBottom>
              Welcome Back
            </Typography>
            <Typography variant="body1" fontSize="24px" fontWeight="600" gutterBottom>
              Sign in to your account
            </Typography>
            <Box display="flex"
              alignItems="center"
              width="100%">
              <Formik<LoginParams>
                initialValues={initialValues}
                validationSchema={loginSchema}
                onSubmit={handleSubmit}
              >
                {({ values, handleChange, handleBlur, errors, touched, isValid }) => (
                  <Form style={{ width: '100%' }}>
                    <StyledForm>
                      <CustomInput
                        label="Email"
                        placeholder="Enter your email."
                        value={values.email}
                        onChange={handleChange('email')}
                        onBlur={handleBlur('email')}
                        error={touched.email && errors.email}
                        disabled={isLoading}
                        fullWidth
                      />
                      <CustomInput
                        type="password"
                        label="Password"
                        placeholder="············"
                        value={values.password}
                        onChange={handleChange('password')}
                        onBlur={handleBlur('password')}
                        error={touched.password && errors.password}
                        disabled={isLoading}
                        fullWidth
                      />
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <CustomInput type="checkbox" label="Remember Me" />
                        <StyledLink onClick={() => navigate('/forget-password')}>
                          Forgot Your Password?
                        </StyledLink>
                      </Box>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={!isValid || isLoading}
                        fullWidth
                      >
                        {isLoading && <CircularProgress
                          sx={{ color: 'white', marginRight: '15px' }}
                        />}
                        {isLoading ? 'Signing In' : 'Sign In'}
                      </Button>
                      {/* <Typography variant="body1">
                        Don&apos;t Have an account?{' '}
                        <StyledLink onClick={() => navigate('/signup')}>
                          Create an account
                        </StyledLink>
                      </Typography> */}
                      <StyledLink onClick={() => window.open('https://help.gonzy.com/en/', '_blank')} rel="noopener noreferrer">
                          Need Help?
                        </StyledLink>
                    </StyledForm>
                  </Form>
                )}
              </Formik>
            </Box>
          </FormContainer>
        </PageContainer>
      </StyledPage>
      <ErrorModal />
      <RouteChangeToastCleaner />
    </AppTheme>
  );
};

export default LoginPage;
